import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  FormCurrencyField,
  FormDatePicker,
  FormProps,
  FormSelect,
  FormTextField
} from '../../../lib/components';
import * as Yup from 'yup';
import { Typography } from '@mui/material';
import Util from '../../../misc/util';
import { KeyofNamespace } from '../../../translations/keyofNs';
import { useStore } from '../../../hooks/useStore';
import { FormikValues } from 'formik';
import { OperationEnum } from '@pxtra-client-lib-legacy/gen';
import { LoadingDialog } from '../../benefits/customBenefitDialogs';

export type BudgetFormProps = Omit<FormProps, 'children' | 'onSubmit'> & {
  selectedEmployeeIds: string[];
  onTypeChanged?: (type?: string) => void;
  onValueChanged?: (value?: string | number) => void;
  onDateChanged?: (date?: string) => void;
  additionalSubmit?: (values?: FormikValues) => void;
  onSubmitFailed?: (error?: unknown) => void;
};

export const BenefitBudgetForm: FC<BudgetFormProps> = (props) => {
  const { t } = useTranslation();
  const { employeeStore } = useStore();
  const options = ['fixed_value', 'percent_value'].map((o) => ({
    value: o,
    text: t(`common:${o.toUpperCase()}` as KeyofNamespace<'common'>)
  }));
  const [isUpdatingBudgets, setIsUpdatingBudgets] = useState<boolean>(false);

  return (
    <>
      <Form
        initialValues={{
          fixed_value: '',
          percent_value: '',
          method: 'fixed_value',
          start_date: Util.getNextMonth()
        }}
        validationSchema={{
          method: Yup.string().required(t('form:FIELD_IS_REQUIRED')),
          fixed_value: Yup.number().when('method', {
            is: 'fixed_value',
            then: Yup.number()
              .typeError(t('form:INVALID_NUMBER'))
              .positive(t('form:MUST_BE_GREATER_THAN', { min: 0 }))
              .required(t('form:FIELD_IS_REQUIRED'))
          }),
          percent_value: Yup.number().when('method', {
            is: 'percent_value',
            then: Yup.number()
              .typeError(t('form:INVALID_NUMBER'))
              .positive(t('form:MUST_BE_GREATER_THAN', { min: 0 }))
              .required(t('form:FIELD_IS_REQUIRED'))
          }),
          start_date: Yup.date().min(
            Util.getFirstDayOfMonth(),
            t('form:DATE_MUST_NOT_BE_IN_THE_PAST')
          )
        }}
        {...props}
        onSubmit={async (values) => {
          try {
            const accountingPeriod = Util.getAccountingPeriod(new Date(values.start_date));

            const keys = Util.getKeys(values).filter((k) => k !== 'start_date');
            const value = keys.filter((k) => !!values[k]).map((k) => values[k])[0] as number;
            const type = keys
              .filter((k) => !!values[k])
              .map((k) => typeof k === 'string' && k.toUpperCase())[0] as OperationEnum;

            setIsUpdatingBudgets(true);
            await employeeStore.updateBenefitBudgets(
              props.selectedEmployeeIds,
              value,
              type,
              accountingPeriod.month,
              accountingPeriod.year
            );
            setIsUpdatingBudgets(false);
            props.additionalSubmit?.(values);
          } catch (error) {
            if (props.onSubmitFailed) {
              props.onSubmitFailed(error);
            } else {
              console.error(error);
            }
          } finally {
            setIsUpdatingBudgets(false);
          }
        }}
      >
        <FormSelect
          additionalOnChange={(value) => props.onTypeChanged?.((value as string).toUpperCase())}
          name="method"
          label={t('form:BUDGET_UPDATE_TYPE')}
          placeholder={t('form:BUDGET_UPDATE_TYPE')}
          options={options}
          xs={4}
        />
        <FormCurrencyField
          additionalOnChange={(value) => props.onValueChanged?.(parseFloat(value as string))}
          name="fixed_value"
          label={t('common:FIXED_VALUE')}
          isHidden={(values) => values.method !== 'fixed_value'}
          xs={4}
          shiftDecimals={2}
          returnInteger={true}
        />
        <FormTextField
          additionalOnChange={(value) => props.onValueChanged?.(parseFloat(value as string))}
          label={t('common:PERCENT_VALUE')}
          endAdornment={<Typography color="grey.900">%</Typography>}
          placeholder="0,00"
          name="percent_value"
          type="number"
          isHidden={(values) => values.method !== 'percent_value'}
          xs={4}
        />
        <FormDatePicker
          variant="month"
          openTo="month"
          additionalOnChange={(value) => props.onDateChanged?.(value as string)}
          name="start_date"
          label={t('template:SELECT_DATE')}
          minDate={Util.getNextMonth()}
          xs={4}
        />
      </Form>
      {isUpdatingBudgets && (
        <LoadingDialog text={t('common:CHANGE_BUDGET_LOADING_HINT')} open={isUpdatingBudgets} />
      )}
    </>
  );
};
